import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { formatRange, formatHM } from "@/lib/time";
import { getCategory } from "@/lib/categories";
import { deleteEvent } from "@/lib/storage";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

export default function EventDetailDialog({ open, onOpenChange, event }) {
  const { t } = useTranslation();
  if (!event) return null;
  const cat = getCategory(event.category);

  const onDelete = () => {
    deleteEvent(event.id);
    toast.success(t("eventDetail.toastDeleted"));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="event-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span
              className="h-9 w-9 rounded-lg border border-black/5 shrink-0"
              style={{ backgroundColor: cat.hex }}
              aria-hidden
            />
            <div className="min-w-0">
              <DialogTitle className="font-display tracking-tight truncate" data-testid="event-detail-title">
                {event.title}
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5 font-mono-tab" data-testid="event-detail-range">
                {formatRange(event)}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Start / end timeline indicators — start time above its divider, end time below its divider */}
          <div className="rounded-[16px] border border-border bg-muted/40 px-4 py-3" data-testid="event-detail-timeline">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{t("eventDetail.start")}</p>
              <p className="text-lg font-mono-tab font-semibold text-slate-900 leading-tight" data-testid="event-detail-start">
                {formatHM(event.startHour, event.startMinute)}
              </p>
              <div className="border-t border-slate-300 mt-1.5" />
            </div>
            <div className="mt-3">
              <div className="border-t border-slate-300 mb-1.5" />
              <p className="text-lg font-mono-tab font-semibold text-slate-900 leading-tight" data-testid="event-detail-end">
                {formatHM(event.endHour, event.endMinute)}
              </p>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{t("eventDetail.end")}</p>
            </div>
          </div>

          <Row label={t("eventDetail.category")} value={t(`categories.${event.category}`)} testid="event-detail-category" />
          <Row label={t("eventDetail.priority")} value={t("eventDetail.priorityValue", { priority: event.priority })} testid="event-detail-priority" />
          {event.description ? (
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{t("eventDetail.description")}</p>
              <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap" data-testid="event-detail-description">
                {event.description}
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={onDelete}
            data-testid="event-detail-delete"
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> {t("eventDetail.delete")}
          </Button>
          <Button
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            data-testid="event-detail-close"
          >
            {t("eventDetail.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900" data-testid={testid}>{value}</span>
    </div>
  );
}
